import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  AuthContext,
  ContextualChatContext,
  ExternalAuthContext,
  LayoutContext,
} from 'contexts';
import { useBreakpoint } from 'hooks';
import {
  featureEnabled,
  isContextualChatEnabled,
  mobileBarRowHeight as rowHeight,
} from 'utils';
import { Icon } from '@brokerbay/elements-web';
import { BadgeParent } from '../../Router/components/Chat/BadgeParent';
import UniversalSearch from '../UniversalSearch';
import Files from './Files';
import UserMenu from './UserMenu';
import SupportMenu from './SupportMenu';
import Notifications from './Notifications';
import Messages from './Messages';
import ChatStatusBadge from './ChatStatusBadge';
import MobileBar from './MobileBar';
import { StyledHeader, StyledHeaderContent } from './StyledComponents';

const iconSizes = {
  height: 20,
  width: 20,
};

const AppHeader: React.FC = () => {
  const {
    getSiderCurrentWidth,
    offsetTop,
    navHeight: height,
    transition,
    appHeaderVisibility,
  } = useContext(LayoutContext);

  const { auth, hasFeature = () => false } = useContext(AuthContext);
  const { auth: externalAuth } = useContext(ExternalAuthContext);
  const { setChatModalVisible, unreadCount } = useContext(
    ContextualChatContext,
  );
  const { md } = useBreakpoint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pageTop, setPageTop] = useState(true);

  const onScroll = () => {
    const { pageYOffset } = window;
    setPageTop(!pageYOffset);
  };

  const showExternalSearch = useMemo(
    () => Boolean(externalAuth?.coopDashboard || externalAuth?.retsDashboard),
    [externalAuth],
  );

  const showingsFeatureOn = useMemo(
    () =>
      featureEnabled('showingsMadeEasyFeature') &&
      !showExternalSearch &&
      auth?.org?.type === 'brokerage',
    [auth, showExternalSearch],
  );

  const _buildUniversalSearch = useMemo(() => {
    if (!auth && !showExternalSearch) return null;

    if (!auth && showExternalSearch)
      return <UniversalSearch orgType="brokerage" external />;

    if (
      auth?.org?.type === 'mlsStaff' &&
      !auth?.org?.features?.includes('MLS_STAFF_SEARCH')
    )
      return null;

    if (
      auth?.org?.type === 'brokerage' &&
      hasFeature &&
      hasFeature('APPOINTMENT')
    )
      return <UniversalSearch orgType="brokerage" />;

    if (auth && auth.org?.type !== 'brokerage')
      return <UniversalSearch orgType="answering" />;
  }, [auth, showExternalSearch, hasFeature]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <StyledHeader
      $showShadow={!mobileMenuOpen}
      $marginTop={offsetTop}
      $marginLeft={getSiderCurrentWidth()}
      $transition={transition}
      $height={height}
      $pageTop={pageTop}
      $visible={appHeaderVisibility}
      $hasShowingsMadeEasyFeature={showingsFeatureOn}
      $hasSupraRebrandingFeature={featureEnabled('supraRebrandingFeature', 1)}
    >
      {!md && (
        <MobileBar
          $height={rowHeight}
          setMobileMenuOpen={setMobileMenuOpen}
          mobileMenuOpen={mobileMenuOpen}
          hideMobileMenu={!auth}
          showingsFeatureOn={showingsFeatureOn}
        />
      )}
      <StyledHeaderContent
        $isMobile={!md}
        $isHidden={!md && !mobileMenuOpen}
        $height={rowHeight}
        $isAssetsHidden={!hasFeature('ASSETS')}
        $isChatHidden={!hasFeature('CHAT')}
        $isExternal={showExternalSearch}
      >
        {(auth || showExternalSearch) && ( // The Universal Search may render in the external coop/rets dashboard
          <div className="grid">
            {md && <div>{_buildUniversalSearch}</div>}
            {auth && ( // This shouldn't render in the external routes
              <>
                <div>
                  {md &&
                    hasFeature('CHAT') &&
                    auth.isChatAdmin &&
                    auth.org.type === 'brokerage' &&
                    !isContextualChatEnabled(auth.org.mlsTypes) && (
                      <ChatStatusBadge />
                    )}
                </div>
                <SupportMenu
                  isMobile={!md}
                  iconSizes={iconSizes}
                  $height={rowHeight}
                  showingsFeatureOn={showingsFeatureOn}
                />
                {hasFeature('ASSETS') && (
                  <Files
                    isMobile={!md}
                    iconSizes={iconSizes}
                    closeMobileMenu={() => setMobileMenuOpen(false)}
                    $height={rowHeight}
                    showingsFeatureOn={showingsFeatureOn}
                  />
                )}
                <Notifications
                  isMobile={!md}
                  iconSizes={iconSizes}
                  closeMobileMenu={() => setMobileMenuOpen(false)}
                  $height={rowHeight}
                  showingsFeatureOn={showingsFeatureOn}
                />
                {hasFeature('CHAT') && (
                  <>
                    {auth.streamChatId &&
                    isContextualChatEnabled(auth?.org.mlsTypes) ? (
                      <button
                        className="app-header-button"
                        onClick={() => setChatModalVisible(true)}
                      >
                        <BadgeParent countNode={unreadCount}>
                          <Icon
                            icon="messageFilled"
                            color="white"
                            size={24}
                            style={{ verticalAlign: '-0.3em' }}
                          />
                        </BadgeParent>
                      </button>
                    ) : (
                      <Messages
                        iconSizes={iconSizes}
                        isMobile={!md}
                        closeMobileMenu={() => setMobileMenuOpen(false)}
                        $height={rowHeight}
                        showingsFeatureOn={showingsFeatureOn}
                      />
                    )}
                  </>
                )}
                <UserMenu
                  isMobile={!md}
                  $height={rowHeight}
                  showingsFeatureOn={showingsFeatureOn}
                />
              </>
            )}
          </div>
        )}
      </StyledHeaderContent>
    </StyledHeader>
  );
};

export default AppHeader;
